import { createReducer, on } from '@ngrx/store';
import * as SquareOrdersActions from './square-orders.actions';
import { SquareOrders } from './square-orders.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface SquareOrdersState {
    squareOrders: SquareOrders[];
    squareOrder: SquareOrders | null;
    totalCount: number;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialSquareOrdersState: SquareOrdersState = {
    squareOrders: [],
    squareOrder: null,
    totalCount: 0,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

export const squareOrdersReducer = createReducer(
    initialSquareOrdersState,

    // Load all
    on(SquareOrdersActions.loadSquareOrders, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareOrdersActions.loadSquareOrdersSuccess,
        (state, { squareOrders, totalCount }) => ({
            ...state,
            isLoading: false,
            squareOrders,
            totalCount,
        })
    ),
    on(SquareOrdersActions.loadSquareOrdersFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Load single
    on(SquareOrdersActions.loadSquareOrder, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareOrdersActions.loadSquareOrderSuccess,
        (state, { squareOrder }) => ({
            ...state,
            isLoading: false,
            squareOrder,
        })
    ),
    on(SquareOrdersActions.loadSquareOrderFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Create
    on(SquareOrdersActions.createSquareOrder, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareOrdersActions.createSquareOrderSuccess,
        (state, { squareOrder }) => ({
            ...state,
            isLoading: false,
            squareOrders: [...(state.squareOrders || []), squareOrder],
        })
    ),
    on(SquareOrdersActions.createSquareOrderFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Update
    on(SquareOrdersActions.updateSquareOrder, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareOrdersActions.updateSquareOrderSuccess,
        (state, { squareOrder }) => ({
            ...state,
            isLoading: false,
            squareOrders: state.squareOrders.map((s) =>
                s.id === squareOrder.id ? squareOrder : s
            ),
        })
    ),
    on(SquareOrdersActions.updateSquareOrderFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Delete
    on(SquareOrdersActions.deleteSquareOrder, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareOrdersActions.deleteSquareOrderSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        squareOrders: state.squareOrders.filter((s) => s.id !== id),
    })),
    on(SquareOrdersActions.deleteSquareOrderFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    })),

    // Revert
    on(SquareOrdersActions.revertOrders, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SquareOrdersActions.revertOrdersSuccess, (state, { data }) => ({
        ...state,
        isLoading: false,
        squareOrders: state.squareOrders.map((order) => {
            const result = (data?.results ?? []).find(
                (r: any) => r.square_order_id === order.id
            );
            return result ? { ...order, stage: result.stage } : order;
        }),
    })),
    on(SquareOrdersActions.revertOrdersFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error,
    }))
);
